// US-013: Join a locked meeting using an access code

import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

export function AccessCode({
  meetingName,
  onSubmit,
  onCancel,
}: {
  meetingName: string
  onSubmit: (code: string) => void
  onCancel: () => void
}) {
  const t = useTranslation();
  const [code, setCode] = useState("")
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (!code.trim()) return
    setLoading(true)
    setError(false)
    // Simulate validation delay
    await new Promise((r) => setTimeout(r, 800))
    setLoading(false)
    if (code.trim().length < 4) {
      setError(true)
      return
    }
    onSubmit(code.trim())
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-vc-surface px-4 text-vc-text">
      <div className="w-full max-w-sm rounded-3xl bg-card/5 p-8 ring-1 ring-white/10">
        <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/20">
          <svg className="h-7 w-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>

        <h1 className="text-center text-xl font-bold text-white">{t("extraFiles.meetingIsLocked")}</h1>
        <p className="mt-2 text-center text-sm text-white/60">
          {t("extraFiles.enterAccessCodeToJoin")} <span className="font-semibold text-white">{meetingName}</span>
        </p>

        <div className="mt-6">
          <input
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(false) }}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit().catch(() => { }) }}
            placeholder={t("extraFiles.accessCode")}
            autoFocus
            className={[
              "w-full rounded-xl bg-card/5 px-4 py-3 text-center font-mono text-lg font-semibold tracking-widest text-white ring-1 placeholder:text-white/30 transition focus:outline-none focus:ring-2",
              error
                ? "ring-red-500/50 focus:ring-red-500"
                : "ring-white/10 focus:ring-indigo-500",
            ].join(" ")}
          />
          {error && (
            <p className="mt-2 text-center text-xs text-red-400">
              {t("extraFiles.invalidAccessCode")}
            </p>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl bg-card/5 py-3 text-sm font-semibold text-white/80 ring-1 ring-white/10 transition hover:bg-card/10"
          >
            {t("extraFiles.cancel")}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit().catch(() => { })}
            disabled={!code.trim() || loading}
            className="rounded-xl bg-indigo-500 py-3 text-sm font-semibold text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? t("extraFiles.checking") : t("extraFiles.join")}
          </button>
        </div>
      </div>
    </div>
  )
}
